import { useId, useState, type KeyboardEvent } from 'react';
import type { BrandAssets, BrandColor, BrandFont } from '@mg/shared';
import { Hash, Image as ImageIcon, Palette, Plus, Quote, Type, X } from 'lucide-react';
import { Card, CardBody, CardHeader } from '../ui/Card';
import { Button } from '../ui/Button';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Input } from '../ui/Field';
import { FontFamilySelect, FontWeightSelect } from './FontFields';
import { useFontCatalog } from './fontCatalog';
import { emptyColor } from './brandAssetsDefaults';
import { LogoSlot } from './LogoSlot';

type ColorGroup = 'primary' | 'secondary';
type FontRole = 'headings' | 'body' | 'accent';

const FONT_ROLES: { key: FontRole; label: string }[] = [
  { key: 'headings', label: 'Headings' },
  { key: 'body', label: 'Body' },
  { key: 'accent', label: 'Accent' },
];

/**
 * Editable brand-assets sections (colors, typography, hashtags, phrases, logos).
 * Fully controlled: the parent owns the `BrandAssets` value and persists it as
 * part of the workspace PATCH. Each section is a titled card with a lucide icon.
 */
export function BrandAssetsEditor({
  value,
  onChange,
  disabled,
}: {
  value: BrandAssets;
  onChange: (next: BrandAssets) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-4">
      <ColorsCard value={value} onChange={onChange} disabled={disabled} />
      <TypographyCard value={value} onChange={onChange} disabled={disabled} />
      <HashtagsCard value={value} onChange={onChange} disabled={disabled} />
      <PhrasesCard value={value} onChange={onChange} disabled={disabled} />
      <VisualAssetsCard value={value} onChange={onChange} disabled={disabled} />
    </div>
  );
}

type SectionProps = {
  value: BrandAssets;
  onChange: (next: BrandAssets) => void;
  disabled?: boolean;
  /**
   * The Edit Brand Workspace v2 look (the Brand assets and style page, O18): the card's title as
   * plain text, without its icon, and this one-line description under it. Left out, the card looks
   * exactly as it always has, as it does inside BrandAssetsEditor.
   */
  plainHeader?: { description: string };
};

/** A card's header: today's icon title, or with `plainHeader` the plain title and its description. */
function SectionHeader({
  icon,
  label,
  plainHeader,
}: {
  icon: typeof Palette;
  label: string;
  plainHeader?: { description: string };
}) {
  if (plainHeader) return <CardHeader title={label} description={plainHeader.description} />;
  return <CardHeader title={<IconTitle icon={icon} label={label} />} />;
}

/* -------------------------------------------------------------------------- */
/* Brand Colors                                                               */
/* -------------------------------------------------------------------------- */

/** Color Schemes: the primary and secondary colors. Also drawn on its own by the v2 Brand assets page. */
export function ColorsCard({ value, onChange, disabled, plainHeader }: SectionProps) {
  function setGroup(group: ColorGroup, rows: BrandColor[]) {
    onChange({ ...value, colors: { ...value.colors, [group]: rows } });
  }

  function updateRow(group: ColorGroup, index: number, patch: Partial<BrandColor>) {
    setGroup(
      group,
      value.colors[group].map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  }

  function addRow(group: ColorGroup) {
    setGroup(group, [...value.colors[group], emptyColor()]);
  }

  function removeRow(group: ColorGroup, index: number) {
    setGroup(
      group,
      value.colors[group].filter((_, i) => i !== index),
    );
  }

  return (
    <Card>
      <SectionHeader icon={Palette} label="Color Schemes" plainHeader={plainHeader} />
      <CardBody className="space-y-5">
        {(['primary', 'secondary'] as const).map((group) => (
          <ColorGroupEditor
            key={group}
            heading={group === 'primary' ? 'Primary Colors' : 'Secondary Colors'}
            rows={value.colors[group]}
            disabled={disabled}
            onAdd={() => addRow(group)}
            onRemove={(i) => removeRow(group, i)}
            onUpdate={(i, patch) => updateRow(group, i, patch)}
          />
        ))}
      </CardBody>
    </Card>
  );
}

function ColorGroupEditor({
  heading,
  rows,
  disabled,
  onAdd,
  onRemove,
  onUpdate,
}: {
  heading: string;
  rows: BrandColor[];
  disabled?: boolean;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onUpdate: (index: number, patch: Partial<BrandColor>) => void;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-semibold text-ink-strong">{heading}</legend>
      {rows.length === 0 && (
        <p className="text-xs text-ink-faint">No colors yet — add one below.</p>
      )}
      <ul className="space-y-2">
        {rows.map((row, i) => (
          <li key={i} className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
            <input
              type="color"
              value={normalizeHex(row.hex)}
              disabled={disabled}
              onChange={(e) => onUpdate(i, { hex: e.target.value })}
              aria-label={`${heading} swatch ${i + 1}`}
              className="focus-ring h-9 w-9 shrink-0 cursor-pointer rounded-lg border border-line-input bg-card disabled:cursor-not-allowed"
            />
            {/* Wrap each Input in a sizing box: the Input is w-full (controlBase) and
                fills the box, so its width is controlled here without a Tailwind conflict. */}
            <div className="min-w-0 flex-1">
              <Input
                value={row.name}
                disabled={disabled}
                onChange={(e) => onUpdate(i, { name: e.target.value })}
                placeholder="Name"
                aria-label={`${heading} name ${i + 1}`}
              />
            </div>
            <div className="w-full shrink-0 sm:w-28">
              <Input
                value={row.hex}
                disabled={disabled}
                onChange={(e) => onUpdate(i, { hex: e.target.value })}
                placeholder="#000000"
                aria-label={`${heading} hex ${i + 1}`}
              />
            </div>
            <div className="min-w-0 flex-[1.5]">
              <Input
                value={row.usage}
                disabled={disabled}
                onChange={(e) => onUpdate(i, { usage: e.target.value })}
                placeholder="Usage"
                aria-label={`${heading} usage ${i + 1}`}
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="shrink-0"
              disabled={disabled}
              onClick={() => onRemove(i)}
              aria-label={`Remove ${heading} color ${i + 1}`}
            >
              <X className="h-4 w-4" aria-hidden />
            </Button>
          </li>
        ))}
      </ul>
      <Button type="button" size="sm" variant="secondary" disabled={disabled} onClick={onAdd}>
        <Plus className="h-3.5 w-3.5" aria-hidden />
        Add color
      </Button>
    </fieldset>
  );
}

/* -------------------------------------------------------------------------- */
/* Typography                                                                 */
/* -------------------------------------------------------------------------- */

/** Fonts/Typography: the three roles. Also drawn on its own by the v2 Brand assets page. */
export function TypographyCard({ value, onChange, disabled, plainHeader }: SectionProps) {
  const baseId = useId();

  /**
   * The families the design server has font FILES for — five of them.
   *
   * Not the picker's list (that is the Google catalog, `useFontCatalog`). This is only how we know
   * whether a chosen family can be MEASURED when generating a design. Advisory: failing to load
   * shows nothing rather than an error.
   */
  const fontsQuery = useQuery({
    queryKey: ['design-fonts'],
    queryFn: ({ signal }) => api.listDesignFonts(signal),
    staleTime: 60 * 60 * 1000,
  });
  const installed = fontsQuery.data?.families ?? [];
  const { families: catalog, loaded: catalogLoaded } = useFontCatalog();

  // Case-insensitively, with the weight suffix ignored, because "Barlow Condensed Bold" is Barlow
  // Condensed — mirroring the normalisation the server applies when it resolves a face.
  const norm = (f: string) =>
    f
      .trim()
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+(thin|extralight|ultralight|light|regular|book|medium|semibold|demibold|bold|extrabold|ultrabold|black|heavy|italic|oblique)$/i, '')
      .replace(/\s+/g, ' ')
      .toLowerCase();

  const isInstalled = (family: string) =>
    installed.length === 0 || installed.some((f) => norm(f) === norm(family));
  const isGoogleFont = (family: string) => catalog.some((f) => norm(f) === norm(family));

  /**
   * Families that will be measured with a substitute, and those we don't recognise at all.
   *
   * Two tiers, because they are two different situations and the old single amber warning would
   * now fire on almost every choice: a Google font renders correctly for anyone (it just isn't
   * installed HERE, so line breaks are estimated), while a family in neither list is probably a
   * licensed brand font — still legitimate, still written into the file, but worth flagging once.
   */
  const chosen = FONT_ROLES.map(({ key }) => value.typography[key].fontFamily.trim()).filter(Boolean);
  const unique = [...new Set(chosen)];
  const substituted = unique.filter((f) => !isInstalled(f) && isGoogleFont(f));
  const unrecognised = catalogLoaded ? unique.filter((f) => !isInstalled(f) && !isGoogleFont(f)) : [];

  function updateRole(role: FontRole, patch: Partial<BrandFont>) {
    onChange({
      ...value,
      typography: { ...value.typography, [role]: { ...value.typography[role], ...patch } },
    });
  }

  return (
    <Card>
      <SectionHeader icon={Type} label="Fonts/Typography" plainHeader={plainHeader} />
      <CardBody className="space-y-4">
        {FONT_ROLES.map(({ key, label }) => {
          const font = value.typography[key];
          const familyId = `${baseId}-${key}-family`;
          const weightId = `${baseId}-${key}-weight`;
          const usageId = `${baseId}-${key}-usage`;
          return (
            <fieldset key={key} className="space-y-2">
              <legend className="text-sm font-semibold text-ink-strong">{label}</legend>
              <div className="grid gap-2 sm:grid-cols-3">
                <div>
                  <label htmlFor={familyId} className="mb-1 block text-xs text-ink-muted">
                    Font family
                  </label>
                  <FontFamilySelect
                    id={familyId}
                    value={font.fontFamily}
                    disabled={disabled}
                    onChange={(fontFamily) => updateRole(key, { fontFamily })}
                  />
                </div>
                <div>
                  <label htmlFor={weightId} className="mb-1 block text-xs text-ink-muted">
                    Weight
                  </label>
                  <FontWeightSelect
                    id={weightId}
                    value={font.weight}
                    disabled={disabled}
                    onChange={(weight) => updateRole(key, { weight })}
                  />
                </div>
                <div>
                  <label htmlFor={usageId} className="mb-1 block text-xs text-ink-muted">
                    Usage
                  </label>
                  <Input
                    id={usageId}
                    value={font.usage}
                    disabled={disabled}
                    onChange={(e) => updateRole(key, { usage: e.target.value })}
                    placeholder="Where it's used"
                  />
                </div>
              </div>
            </fieldset>
          );
        })}

        {substituted.length > 0 && (
          <p className="text-xs text-ink-muted">
            {substituted.join(', ')} {substituted.length === 1 ? 'is' : 'are'} not installed on the
            design server, so line breaks in generated designs are estimated with a close
            substitute. The font is still named in the file and renders correctly everywhere else.
          </p>
        )}
        {unrecognised.length > 0 && (
          <p className="text-xs text-amber-700 dark:text-warning-dark">
            {unrecognised.join(', ')} {unrecognised.length === 1 ? 'is' : 'are'} not a Google font.
            That is fine for a licensed brand typeface — it is written into every file we produce —
            but anyone opening those files without the font installed will see a substitute.
          </p>
        )}
      </CardBody>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Approved Hashtags                                                          */
/* -------------------------------------------------------------------------- */

function HashtagsCard({ value, onChange, disabled }: SectionProps) {
  const inputId = useId();
  const [draft, setDraft] = useState('');

  function addTag() {
    const normalized = normalizeHashtag(draft);
    if (!normalized) return;
    if (!value.hashtags.includes(normalized)) {
      onChange({ ...value, hashtags: [...value.hashtags, normalized] });
    }
    setDraft('');
  }

  function removeTag(tag: string) {
    onChange({ ...value, hashtags: value.hashtags.filter((t) => t !== tag) });
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  }

  return (
    <Card>
      <CardHeader title={<IconTitle icon={Hash} label="Social Hashtags" />} />
      <CardBody className="space-y-3">
        {value.hashtags.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {value.hashtags.map((tag) => (
              <li key={tag}>
                <span className="inline-flex items-center gap-1 rounded-full bg-tint px-2.5 py-1 text-xs font-medium text-tint-fg">
                  {tag}
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => removeTag(tag)}
                    aria-label={`Remove ${tag}`}
                    className="focus-ring rounded-full text-tint-fg/60 hover:text-tint-fg disabled:opacity-50"
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <label htmlFor={inputId} className="sr-only">
            Add hashtag
          </label>
          <Input
            id={inputId}
            value={draft}
            disabled={disabled}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Add a hashtag and press Enter"
            className="flex-1"
          />
          <Button type="button" variant="secondary" disabled={disabled} onClick={addTag}>
            Add
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Approved Phrases                                                           */
/* -------------------------------------------------------------------------- */

function PhrasesCard({ value, onChange, disabled }: SectionProps) {
  function updatePhrase(index: number, text: string) {
    onChange({
      ...value,
      phrases: value.phrases.map((p, i) => (i === index ? text : p)),
    });
  }

  function addPhrase() {
    onChange({ ...value, phrases: [...value.phrases, ''] });
  }

  function removePhrase(index: number) {
    onChange({ ...value, phrases: value.phrases.filter((_, i) => i !== index) });
  }

  return (
    <Card>
      <CardHeader title={<IconTitle icon={Quote} label="Catchphrases & Slogans" />} />
      <CardBody className="space-y-2">
        {value.phrases.length === 0 && (
          <p className="text-xs text-ink-faint">No phrases yet — add one below.</p>
        )}
        <ul className="space-y-2">
          {value.phrases.map((phrase, i) => (
            <li key={i} className="flex items-center gap-2">
              <Input
                value={phrase}
                disabled={disabled}
                onChange={(e) => updatePhrase(i, e.target.value)}
                placeholder="A short approved phrase"
                aria-label={`Phrase ${i + 1}`}
                className="flex-1"
              />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={disabled}
                onClick={() => removePhrase(i)}
                aria-label={`Remove phrase ${i + 1}`}
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
        <Button type="button" size="sm" variant="secondary" disabled={disabled} onClick={addPhrase}>
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Add phrase
        </Button>
      </CardBody>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Visual Assets (logos)                                                      */
/* -------------------------------------------------------------------------- */

/** Logos & Visual Assets: the three logo slots. Also drawn on its own by the v2 Brand assets page. */
export function VisualAssetsCard({ value, onChange, disabled, plainHeader }: SectionProps) {
  function setLogo(variant: keyof BrandAssets['logos'], proxyUrl: string | null) {
    onChange({ ...value, logos: { ...value.logos, [variant]: proxyUrl } });
  }

  return (
    <Card>
      <SectionHeader icon={ImageIcon} label="Logos & Visual Assets" plainHeader={plainHeader} />
      <CardBody>
        <div className="grid gap-4 sm:grid-cols-3">
          <LogoSlot
            label="Primary Logo"
            value={value.logos.primary}
            disabled={disabled}
            onChange={(url) => setLogo('primary', url)}
          />
          <LogoSlot
            label="Logo (Dark)"
            value={value.logos.dark}
            backdrop="dark"
            disabled={disabled}
            onChange={(url) => setLogo('dark', url)}
          />
          <LogoSlot
            label="Logo Icon"
            value={value.logos.icon}
            disabled={disabled}
            onChange={(url) => setLogo('icon', url)}
          />
        </div>
      </CardBody>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

function IconTitle({ icon: Icon, label }: { icon: typeof Palette; label: string }) {
  return (
    <span className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-action" aria-hidden />
      {label}
    </span>
  );
}

/** Strip a leading `#` and lowercase/trim, then re-prefix with a single `#`. */
function normalizeHashtag(raw: string): string {
  const cleaned = raw.trim().replace(/^#+/, '').trim();
  return cleaned ? `#${cleaned}` : '';
}

/**
 * Coerce an arbitrary hex string into a value `<input type="color">` accepts
 * (`#rrggbb`). Falls back to black for partial/invalid input so the swatch stays
 * usable while the user types in the adjacent text field.
 */
function normalizeHex(hex: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(hex.trim()) ? hex.trim() : '#000000';
}
